import {
  langauge,
  StatementColumns,
  accountingSystemSpecificSetting,
  ACCOUNTING_CONFIG,
} from "shared";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Tooltip } from "@mui/material";
import { When } from "react-if";
import { useMemo } from "react";

import { Notification } from "../../../../../common/Atoms/Notification";
import { Select } from "../../../../../common/Atoms/Select";
import { useGeneralSettingsForm } from "../../../../hooks/useGeneralSettingsForm";
import { Toggle } from "../../../../../common/Atoms/Toggle";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { classNames } from "../../../../../common/lib/classNames";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import { HelpIcon } from "../../../Molecules/HelpIcon";
import { useSettings } from "../../../../hooks/useSettings";

const MAX_STATEMENT_COLUMNS = 8;

interface StatementColumnsEditorProps {
  field: `statementColumns` | `portalStatementColumns`;
  // The portal always truncates long values, so only the PDF statement uses this
  showTruncate?: boolean;
}

export function StatementColumnsEditor({ field, showTruncate }: StatementColumnsEditorProps) {
  const { formData, onChange } = useGeneralSettingsForm();
  const { data: settings } = useSettings();
  const currentOrg = useGetSelectedOrganisation();
  const columns = formData[field];

  const columnOptions = useMemo(() => {
    const options = [...Object.keys(StatementColumns).map(key => {
      return {
        value: StatementColumns[key],
        label: langauge.settings.statement.statementColumnSettings[StatementColumns[key]].label,
      };
    })].filter(x => {
      if (!accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_INVOICE_PRIVATE_NOTE) && x.value === StatementColumns.QBOPrivateNote) {
        return false;
      }

      return true;
    });

    if (settings?.invoiceChildFields && settings.invoiceChildFields.length > 0) {
      settings.invoiceChildFields.forEach(field => {
        options.push({
          value: field.name,
          label: `${field.name} (Custom)`,
        });
      });
    }

    return options;
  }, [currentOrg, settings]);

  const showCustomFieldWarning = useMemo(() => {
    if (settings?.displayCustomFields) {
      return false;
    }

    const selectedColumns = columns?.map(c => c.type) || [];

    const staticColumns = Object.values(StatementColumns);

    return selectedColumns.some(c => c && !staticColumns.includes(c as StatementColumns));
  }, [columns, settings]);

  function onColumnChange(columnIndex: number, value: StatementColumns | null) {
    if (!columns) {
      return;
    }

    const newColumns = [...columns];

    newColumns[columnIndex] = {
      type: value,
      truncate: newColumns[columnIndex]?.truncate,
    };

    const removedNulls = newColumns.filter(c => c.type !== null);

    onChange(field, removedNulls);
  }

  function onTruncateChange(columnIndex: number, value: boolean) {
    if (!columns) {
      return;
    }

    if (!columns[columnIndex]?.type) {
      return;
    }

    const newColumns = [...columns];

    newColumns[columnIndex] = {
      type: newColumns[columnIndex]?.type,
      truncate: value,
    };

    onChange(field, newColumns);
  }

  return (
    <div>
      <div className={ `grid grid-cols-7 mb-2` }>
        <Paragraph
          variant={ `subHeading` }
          className={ `col-span-2` }
        >
          { `` }
        </Paragraph>

        <Paragraph
          variant={ `subHeading` }
          className={ `col-span-3` }
        >
          { `` }
        </Paragraph>

        <When condition={ !!showTruncate }>
          <div className={ `col-span-1 flex justify-end` }>
            <Paragraph
              variant={ `subHeading` }
              className={ `text-right` }
            >
              { `Truncate` }
            </Paragraph>
            <HelpIcon
              tooltip={ `If the content doesn't fit in the column, it will be truncated. E.g "INV-12345..."` }
            />
          </div>
        </When>
      </div>
      { /* Map through the max statement columns  */ }
      {
        Array.from({ length: MAX_STATEMENT_COLUMNS }, (_, i) => {

          const totalColumnsWithValues = columns?.filter(c => !!c.type).length;

          const showClear = totalColumnsWithValues > 5 && !!columns?.[i]?.type;

          return (
            <div
              key={ `statement-col-${i}` }
              className={ classNames(
                `grid grid-cols-7 p-1 rounded-xs`,
                i % 2 === 0 ? `bg-gray-100` : ``,
              ) }
            >
              <div className={ `col-span-2 flex items-center` }>
                <Paragraph variant={ `secondary` }>
                  { `Column ${i + 1}` }
                </Paragraph>
              </div>

              <div className={ showTruncate ? `col-span-3` : `col-span-4` }>
                <Select
                  selected={ columns?.[i]?.type || null }
                  options={ columnOptions }
                  onChange={ e => onColumnChange(i, e.value) }
                  emptyText={ `Nothing` }
                />
              </div>

              <When condition={ !!showTruncate }>
                <div className={ `col-span-1 flex items-center justify-end` }>
                  <Toggle
                    checked={ columns?.[i]?.truncate || false }
                    onChange={ e => onTruncateChange(i, e.value) }
                    disabled={ !columns?.[i]?.type }
                  />
                </div>
              </When>

              <div className={ `col-span-1 flex justify-end items-center mr-8` }>
                <Tooltip
                  title={
                    showClear ? `Remove Column` : (
                      totalColumnsWithValues <= 5 ? `At least 5 columns must be used` : `Column is already cleared`
                    )
                  }
                >
                  <a
                    onClick={ () => onColumnChange(i, null) }
                    className={ `cursor-pointer` }
                  >
                    <XMarkIcon className={ classNames(
                      `h-5 w-5`,
                      showClear ? `text-red-500` : `text-gray-300`,
                    ) } />

                  </a>
                </Tooltip>
              </div>
            </div>
          );
        })
      }
      <When condition={ showCustomFieldWarning }>
        <Notification
          type={ `warning` }
        >
          { `You have selected a custom field as a column. Ensure that "Display Custom Fields" is enabled under "Portal Settings" to prevent empty values` }
        </Notification>
      </When>
    </div>
  );
}
